import { useEffect, useCallback, useMemo, useRef, useReducer } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Api from '../../../../component/Api';

const PER_PAGE = 10;
const EMPTY_FILTERS = { course: '', discipline: '', status: '', search: '', exam: '' };

const parseHashParams = (hash) => {
    const result = { filters: { ...EMPTY_FILTERS }, page: 1, search: '' };
    const content = hash.startsWith('#') ? hash.slice(1) : hash;

    for (const part of content.split('/')) {
        if (part.startsWith('filter=')) {
            for (const f of part.slice(7).split(',')) {
                const idx = f.indexOf(':');
                if (idx === -1) continue;
                const key = f.slice(0, idx).trim();
                const val = f.slice(idx + 1).trim();
                if (key && val && key in result.filters) {
                    result.filters[key] = decodeURIComponent(val);
                }
            }
        } else if (part.startsWith('page=')) {
            const p = parseInt(part.slice(5), 10);
            if (!isNaN(p) && p > 0) result.page = p;
        } else if (part.startsWith('search=')) {
            result.search = decodeURIComponent(part.slice(7));
        }
    }

    return result;
};

const buildHash = (currentHash, filters, page, search, admissionYear) => {
    const content = currentHash.startsWith('#') ? currentHash.slice(1) : currentHash;

    const base = content
        .split('/')
        .filter(p => p && !p.startsWith('filter=') && !p.startsWith('page=') && !p.startsWith('search=') && !p.startsWith('year='));

    if (admissionYear) base.push(`year=${encodeURIComponent(admissionYear)}`);

    const filterParts = Object.entries(filters)
        .filter(([, v]) => v)
        .map(([k, v]) => `${k}:${encodeURIComponent(v)}`);

    if (filterParts.length) base.push(`filter=${filterParts.join(',')}`);
    if (page > 1) base.push(`page=${page}`);

    return '#' + base.join('/');
};

const initialState = (hash) => {
    const { filters, page } = parseHashParams(hash);
    return {
        students: [],
        loading: false,
        currentPage: page,
        lastPage: 1,
        total: 0,
        filters,
    };
};

const ACTION = {
    FETCH_SUCCESS: 'FETCH_SUCCESS',
    FETCH_ERROR: 'FETCH_ERROR',
    SET_PAGE: 'SET_PAGE',
    SET_FILTERS: 'SET_FILTERS',
    CLEAR_FILTERS: 'CLEAR_FILTERS',
    SET_LOADING: 'SET_LOADING',
    RESET: 'RESET',
};

const reducer = (state, { type, payload }) => {
    switch (type) {
        case ACTION.FETCH_SUCCESS:
            return {
                ...state,
                loading: false,
                students: payload.data,
                currentPage: payload.current_page,
                lastPage: payload.last_page,
                total: payload.total,
            };
        case ACTION.FETCH_ERROR:
            return { ...state, loading: false, students: [] };
        case ACTION.SET_LOADING:
            return { ...state, loading: true };
        case ACTION.SET_PAGE:
            return { ...state, currentPage: payload };
        case ACTION.SET_FILTERS:
            return { ...state, filters: payload, currentPage: 1 };
        case ACTION.CLEAR_FILTERS:
            return { ...state, filters: { ...EMPTY_FILTERS }, currentPage: 1 };
        case ACTION.RESET:
            return { ...initialState(''), loading: false };
        default:
            return state;
    }
};

export const useStudentList = (refreshKey, enabled = true, admissionYear = '') => {
    const { http } = useMemo(() => Api(), []);
    const navigate = useNavigate();
    const location = useLocation();

    const [state, dispatch] = useReducer(reducer, location.hash, initialState);
    const { students, loading, currentPage, lastPage, total, filters } = state;

    const hashRef = useRef(location.hash);
    const pathnameRef = useRef(location.pathname);
    const initializedRef = useRef(false);
    const abortRef = useRef(null);
    const prevEnabledRef = useRef(enabled);
    const prevAdmissionYearRef = useRef(admissionYear);

    useEffect(() => {
        hashRef.current = location.hash;
        pathnameRef.current = location.pathname;
    });

    const syncURL = useCallback((page, search, activeFilters, year) => {
        const newHash = buildHash(hashRef.current, activeFilters, page, search, year);
        navigate(`${pathnameRef.current}${newHash}`, { replace: true });
    }, [navigate]);

    const fetchStudents = useCallback(async (page, search, activeFilters, year, opts = {}) => {
        if (!year) return;

        abortRef.current?.abort();
        abortRef.current = new AbortController();

        if (!opts.silent) dispatch({ type: ACTION.SET_LOADING });

        try {
            const response = await http.get('/is/studentList', {
                params: {
                    page,
                    per_page: PER_PAGE,
                    search: activeFilters.search || '',
                    course: activeFilters.course || '',
                    discipline: activeFilters.discipline || '',
                    status: activeFilters.status || '',
                    exam: activeFilters.exam || '',
                    admission_year: year,
                },
                signal: abortRef.current.signal,
            });

            const data = Array.isArray(response.data?.data) ? response.data.data : [];
            const pagination = response.data?.pagination ?? {};

            dispatch({
                type: ACTION.FETCH_SUCCESS,
                payload: {
                    data,
                    current_page: pagination.current_page,
                    last_page: pagination.last_page,
                    total: pagination.total,
                },
            });

            syncURL(pagination.current_page, search, activeFilters, year);
        } catch (error) {
            if (error.name !== 'AbortError') {
                console.error('[useStudentList] Fetch error:', error);
                dispatch({ type: ACTION.FETCH_ERROR });
            }
        }
    }, [http, syncURL]);

    useEffect(() => {
        if (!enabled || !admissionYear) return;

        const { filters: f, page, search } = parseHashParams(location.hash);
        fetchStudents(page, search, f, admissionYear).then(() => {
            initializedRef.current = true;
        });

        return () => abortRef.current?.abort();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const wasEnabled = prevEnabledRef.current;
        prevEnabledRef.current = enabled;

        if (enabled && !wasEnabled) {
            if (!initializedRef.current && admissionYear) {
                fetchStudents(currentPage, filters.search, filters, admissionYear).then(() => {
                    initializedRef.current = true;
                });
            }
        } else if (!enabled && wasEnabled) {
            abortRef.current?.abort();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled]);

    useEffect(() => {
        const prevYear = prevAdmissionYearRef.current;
        prevAdmissionYearRef.current = admissionYear;

        if (prevYear === admissionYear) return;

        if (!admissionYear) {
            abortRef.current?.abort();
            dispatch({ type: ACTION.RESET });
            syncURL(1, '', { ...EMPTY_FILTERS }, '');
            return;
        }

        dispatch({ type: ACTION.CLEAR_FILTERS });
        initializedRef.current = true;
        fetchStudents(1, '', { ...EMPTY_FILTERS }, admissionYear);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [admissionYear]);

    useEffect(() => {
        if (!initializedRef.current || !enabled || !admissionYear) return;
        fetchStudents(currentPage, filters.search, filters, admissionYear);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshKey]);

    useEffect(() => {
        if (!initializedRef.current || !enabled || !admissionYear) return;
        fetchStudents(1, filters.search, filters, admissionYear);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.course, filters.discipline, filters.status, filters.search, filters.exam]);

    const handlePageChange = useCallback((page) => {
        dispatch({ type: ACTION.SET_PAGE, payload: page });
        fetchStudents(page, filters.search, filters, admissionYear);
    }, [fetchStudents, filters, admissionYear]);

    const handleApplyFilter = useCallback((newFilters) => {
        dispatch({ type: ACTION.SET_FILTERS, payload: newFilters });
    }, []);

    const handleClearFilters = useCallback(() => {
        dispatch({ type: ACTION.CLEAR_FILTERS });
    }, []);

    const hasActiveFilters = useMemo(
        () => !!(filters.course || filters.discipline || filters.status || filters.search || filters.exam),
        [filters]
    );

    return {
        students,
        loading,
        currentPage,
        lastPage,
        total,
        perPage: PER_PAGE,
        appliedFilters: filters,
        hasActiveFilters,
        handlePageChange,
        handleApplyFilter,
        handleClearFilters,
        fetchStudents,
    };
};